import { useCallback, useMemo, useRef, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { FormHandles, Scope } from '@unform/core'
import axios from 'axios'
import i from 'i18next'

import AlertInfo from '~/components/AlertInfo'
import { Button } from '~/components/Buttons'
import HeaderPages from '~/components/HeaderPages'
import Loading from '~/components/Loading'
import Panel from '~/components/Panel'
import { Badge } from '~/components/UI/Badge'
import { FormGroup, Input, Label, Radio, Select } from '~/components/unform'
import Form from '~/components/unform/Form'
import { history } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import PlacementCategory from '~/modules/retailMedia/components/CampaignForm/elements/Targeting/Category'
import { contexts } from '~/modules/retailMedia/constants'
import { getAdSizesService } from '~/modules/retailMedia/services/ad-size'

export const apiNewtailRetailMedia = axios.create({
  baseURL: `https://newtail-media.newtail.com.br/v1/rma`
})

export const getNewtailMedia = async ({ publisherId, body }) =>
  apiNewtailRetailMedia.post(`/${publisherId}`, body)

const RequestTesterPage: React.FC = () => {
  const searchParams = useQueryParams()
  const URLparams = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams]
  )

  const [type, setType] = useState<CampaignAdType>(
    (searchParams.get('type') as CampaignAdType) ?? 'product'
  )
  const [context, setContext] = useState<CampaignContext>(
    (searchParams.get('context') as CampaignContext) ?? 'search'
  )

  const [loadingAds, setLoadingAds] = useState(false)
  const [errosAds, setErrorAds] = useState<Error | null>(null)
  const [ads, setAds] = useState<
    BannerAd[] | ProductAd[] | SponsoredAdsResponse[]
  >()

  const formRef = useRef<FormHandles>(null)

  const contextOptions: RadioOptionProps[] = contexts.map(context => {
    const value = i.t(`rm:context.${context}`) as string

    return {
      label: i.t('common:capitalize', { value }) as string,
      value: context
    }
  })

  /**
   * Options type
   */

  const banner: RadioOptionProps = {
    label: i.t('common:capitalize', {
      value: i.t('rm:banner')
    }),
    value: 'banner',
    name: 'banner'
  }

  const product: RadioOptionProps = {
    label: i.t('common:capitalize', {
      value: i.t('rm:product')
    }),
    value: 'product',
    name: 'product'
  }

  const sponsoredBrand: RadioOptionProps = {
    label: i.t('common:capitalize', {
      value: i.t('rm:sponsored_brand')
    }),
    value: 'sponsored_brand',
    name: 'sponsored_brand'
  }

  const typeOptions: RadioOptionProps[] = [banner, product, sponsoredBrand]

  const { isPending: adSizeIsPending, data: adSizes } = useQuery({
    queryKey: ['get-ad-sizes'],
    queryFn: async () => {
      const res = await getAdSizesService()

      return res.data.data
    }
  })

  const publisherId: PublisherId = useMemo(
    () => adSizes?.[0]?.publisher_id,
    [adSizes]
  )

  const removeUndefined = useCallback(
    (obj: FormDataRequestAds | PayloadRequestAds | PlacementRequest) =>
      Object.fromEntries(
        Object.entries(obj).filter(([key, v]) => v !== undefined)
      ),
    []
  )

  const formatPayload = useCallback(
    (formData: FormDataRequestAds): PayloadRequestAds => ({
      context: formData.context,
      device: 'desktop',
      term: formData.term,
      category_name: formData?.targeting?.categories,
      product_sku: formData.product_sku,
      brand_name: formData.brand_name,
      session_id: `request-tester-${formData.context}`,
      user_id: null,
      brand: formData.brand,
      placements: {
        placement: removeUndefined({
          types: [formData.type],
          quantity: Number(formData.quantity),
          size: formData.size
        }) as PlacementRequest
      }
    }),
    []
  )

  const [curlCommand, setCurlCommand] = useState<string>('')

  const handleSubmit = useCallback(
    (formData: FormData) => {
      const payload = removeUndefined(formatPayload(formData))

      const buildCurlExample = (payload: any) => {
        const url = `https://newtail-media.newtail.com.br/v1/rma/${publisherId}`
        const body = JSON.stringify(payload, null, 2)

        return `curl -X POST "${url}" \\
        -H "Content-Type: application/json" \\
        -d '${body}'`
      }

      setCurlCommand(buildCurlExample(payload))

      setLoadingAds(true)

      setErrorAds(null)

      getNewtailMedia({
        publisherId,
        body: payload
      })
        .then(response => {
          const { placement } = response.data

          setAds(placement)

          console.log('VTEX Ads:', placement)
          setLoadingAds(false)
        })
        .catch(error => {
          setLoadingAds(false)
          setErrorAds(error)
          console.error('Error fetching VTEX Ads:', error)
        })
    },
    [formatPayload, removeUndefined, publisherId]
  )

  /**
   * Handle change
   */

  const handleURLChange = useCallback(
    ({ name, value }: { name: RequestAdsURLParams; value: string }) => {
      const newParams = new URLSearchParams(searchParams.toString())

      if (!value) {
        newParams.delete(name)
        history.push({ search: newParams.toString() })
        return
      }

      if (name === 'type' && value !== 'banner') {
        newParams.delete('size')
        newParams.set(name, String(value))
        history.push({ search: newParams.toString() })
        return
      }

      const contextActions: Record<string, RequestAdsURLParams[]> = {
        home: ['category_name', 'product_sku', 'brand_name', 'term'],
        category: ['product_sku', 'brand_name', 'term'],
        product_page: ['category_name', 'brand_name', 'term'],
        brand_page: ['product_sku', 'brand_name', 'term'],
        search: ['category_name', 'product_sku', 'brand_name']
      }

      if (name === 'context' && contextActions[value]) {
        contextActions[value].forEach(param => newParams.delete(param))
        newParams.set(name, String(value))
        history.push({ search: newParams.toString() })
        return
      }

      newParams.set(name, String(value))
      history.push({ search: newParams.toString() })
    },
    [searchParams]
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target

      handleURLChange({ name, value })
    },
    [handleURLChange]
  )

  const initialData = useMemo(() => {
    return {
      type: type,
      context: context,
      quantity: Number(URLparams.quantity) || 1,
      size: URLparams.size || '',
      targeting: {
        categories: URLparams.category_name || ''
      },
      product_sku: URLparams.product_sku || '',
      brand_name: URLparams.brand_name || '',
      term: URLparams.term || ''
    }
  }, [context, type, URLparams])

  return (
    <>
      <HeaderPages
        title={i.t('rm:Tools.AdRequest.pageTitle')}
        sideLeftComponent={<Badge text="beta" />}
      />
      <Loading status={adSizeIsPending} />

      {!adSizeIsPending && (
        <>
          <Form
            ref={formRef}
            onSubmit={handleSubmit}
            initialData={initialData}
            className="form-grid"
            onChange={handleChange}
          >
            <Panel title={i.t('rm:placements.form.formats.title')}>
              <FormGroup className="span-full">
                <Radio
                  options={typeOptions}
                  name="type"
                  layout="grid"
                  required
                  itemSize="small"
                  onChange={e => setType(e.target.value as CampaignAdType)}
                />
              </FormGroup>

              <FormGroup>
                <Label>{i.t('rm:placements.form.quantityAds.label')}</Label>
                <Input name="quantity" placeholder="Quantity" required />
              </FormGroup>

              {type === 'banner' && (
                <FormGroup>
                  <Label>Size</Label>
                  <Select
                    required
                    name="size"
                    options={adSizes?.map(adSize => ({
                      label: `${adSize.name} (${adSize.width}x${adSize.height})`,
                      value: adSize.name
                    }))}
                    placeholder="Select size"
                  />
                </FormGroup>
              )}
            </Panel>

            <Panel title={i.t('rm:placements.list.table.column.title.context')}>
              <FormGroup>
                <Radio
                  options={contextOptions}
                  name="context"
                  layout="grid"
                  required
                  itemSize="small"
                  onChange={e => setContext(e.target.value as CampaignContext)}
                />
              </FormGroup>
            </Panel>

            <Panel title={i.t('rm:Configurations')}>
              {context === 'search' && (
                <FormGroup>
                  <Label>{i.t('rm:Term')}</Label>
                  <Input required name="term" placeholder="Termo" />
                </FormGroup>
              )}

              {context === 'category' && (
                <Scope path="targeting">
                  <PlacementCategory
                    initialCategories={[
                      {
                        id: initialData?.targeting?.categories,
                        label: initialData?.targeting?.categories
                      }
                    ]}
                    onChange={data =>
                      handleURLChange({
                        name: 'category_name',
                        value: data?.categories?.[0]?.label
                      })
                    }
                    publisherId={publisherId}
                    singleCategory
                    formRef={formRef}
                  />
                </Scope>
              )}

              {context === 'product_page' && (
                <FormGroup>
                  <Label>SKU</Label>
                  <Input
                    required
                    name="product_sku"
                    placeholder="Product SKU"
                  />
                </FormGroup>
              )}

              {context === 'brand_page' && (
                <FormGroup>
                  <Label>Brand name</Label>
                  <Input required name="brand_name" placeholder="Brand Name" />
                </FormGroup>
              )}
            </Panel>

            <footer>
              <Button type="submit" text={i.t('common:actions.confirm')} />
            </footer>
          </Form>

          <Loading status={loadingAds}>Loading ads...</Loading>

          {!!ads && (
            <>
              <hr />

              <Panel title="Ads">
                {!ads?.length && (
                  <AlertInfo
                    template="warning"
                    text={i.t('c:messages.noResultsFound')}
                  />
                )}

                {ads?.[0]?.media_url &&
                  (ads as BannerAd[]).map((ad: BannerAd) => (
                    <div key={ad.ad_id} className="border rounded mb-2 p-2">
                      <img
                        src={ad.media_url}
                        alt={ad.campaign_name}
                        style={{ maxWidth: '100%' }}
                      />
                      <p className="m-0">
                        <strong>
                          {i.t('rm:Campaign')}:{' '}
                          <a
                            target="_blank"
                            href={`/retail-media/campaigns?page=1&campaign_name=${ad.campaign_name}`}
                            rel="noreferrer"
                          >
                            {ad.campaign_name}
                          </a>
                        </strong>
                        <br />
                        {i.t('rm:adDestination')}: {ad.destination_url || '-'}
                      </p>
                    </div>
                  ))}

                {ads?.[0]?.product_name &&
                  (ads as ProductAd[]).map((ad: ProductAd) => (
                    <div key={ad.ad_id} className="border-bottom mb-2 pb-2">
                      <strong>
                        {i.t('rm:Campaign')}: {ad.campaign_name}
                      </strong>
                      <p>
                        <span>product_name: {ad.product_name}</span>
                        <br />
                        <span>product_sku: {ad.product_sku}</span>
                        {ad.seller_id && (
                          <>
                            <br />
                            <span>seller_id: {ad.seller_id}</span>
                          </>
                        )}
                      </p>
                    </div>
                  ))}
                {ads?.[0]?.assets &&
                  (ads as SponsoredAdsResponse[]).map(
                    (ad: SponsoredAdsResponse) => (
                      <div key={ad.ad_id} className="border-bottom mb-2 pb-2">
                        <strong>
                          {i.t('rm:Campaign')}: {ad.campaign_name}
                        </strong>
                        <p>
                          <span>Headline: {ad.headline}</span>
                          <br />
                          <span>Brand name: {ad.brand_name}</span>
                          <br />
                          <span>Description: {ad.description}</span>
                        </p>

                        <img src={ad.brand_url} style={{ maxWidth: '100%' }} />

                        {ad.assets.map(asset => (
                          <img
                            key={asset.url}
                            src={asset.url}
                            alt={asset.type}
                            style={{ maxWidth: '100%' }}
                          />
                        ))}
                      </div>
                    )
                  )}
              </Panel>
            </>
          )}

          {curlCommand && (
            <Panel title="CURL command" isCollapsible>
              <code>
                <pre
                  style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                >
                  {curlCommand}
                </pre>
              </code>
            </Panel>
          )}

          {!!ads?.length && (
            <Panel
              title="Ads JSON"
              isCollapsible
              initialCollapsibleState={false}
            >
              <code>
                <pre
                  style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                >
                  {JSON.stringify(ads)}
                </pre>
              </code>
            </Panel>
          )}
        </>
      )}
    </>
  )
}

export default RequestTesterPage
